import React, { useState, useEffect } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Box, Tooltip, Switch,
    DialogActions, Button, CircularProgress, IconButton,
    Dialog, DialogTitle, DialogContent, TextField,
    Typography
} from '@mui/material';
import API_BASE_URL from '../../config';
import { Fab } from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';

const AdminPackages = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editId, setEditId] = useState(null);
    const [newPackage, setNewPackage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editedPackage, setEditedPackage] = useState({});

    const fetchPackages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/packages`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setPackages(data);
        } catch (err) {
            setError('Failed to load packages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    const handleCreate = async () => {
        setIsProcessing(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/packages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            if (!response.ok) throw new Error('Create failed');
            fetchPackages();
            setNewPackage(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdate = async (pkg) => {
        setIsProcessing(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/packages/${pkg.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(pkg)
            });
            
            if (!response.ok) throw new Error('Update failed');
            fetchPackages();
            setEditId(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleChange = (id, field, value) => {
        if (id === 'new') {
            setNewPackage(prev => ({ ...prev, [field]: value }));
        } else {
            setEditedPackage(prev => ({ ...prev, [field]: value }));
        }
    };

     return (
        <Box sx={{ p: 3, height: '100vh', backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: 'white' }}>Package Management</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />}
                    onClick={() => setNewPackage({
                        package_name: '',
                        device_group: false,
                        time_sync: false,
                        sensors: false,
                        gps: false,
                        temperature: false,
                        brightness: false,
                        media: 0,
                        log_access: false,
                        log_download: false,
                        data_user: false,
                        schedule: false,
                        calendar_schedule: false,
                        video: false,
                        emegency: false,
                        email: false,
                        sms: false
                    })}
                >
                    Add Package
                </Button>
            </Box>

            {error && <Typography color="error">{error}</Typography>}

            <Box sx={{ height: '80%', width: '100%' }}>
                <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', color: 'white' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Device Group</TableCell>
                                <TableCell>Time Sync</TableCell>
                                <TableCell>Sensors</TableCell>
                                <TableCell>GPS</TableCell>
                                <TableCell>Temperature</TableCell>
                                <TableCell>Brightness</TableCell>
                                <TableCell>Media</TableCell>
                                <TableCell>Log Access</TableCell>
                                <TableCell>Log Download</TableCell>
                                <TableCell>Data User</TableCell>
                                <TableCell>Schedule</TableCell>
                                <TableCell>Calendar</TableCell>
                                <TableCell>Video</TableCell>
                                <TableCell>Emergency</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>SMS</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {newPackage && (
                                <TableRow>
                                    <TableCell>
                                        <TextField
                                            value={newPackage.package_name}
                                            onChange={(e) => handleChange('new', 'package_name', e.target.value)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={newPackage.device_group}
                                            onChange={(e) => handleChange('new', 'device_group', e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            onClick={handleCreate}
                                            disabled={isProcessing}
                                        >
                                            Create
                                        </Button>
                                        <Button 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                            onClick={() => setNewPackage(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                            
                            {packages.map((pkg) => (
                                <TableRow key={pkg.id}>
                                    <TableCell>
                                        {editId === pkg.id ? (
                                            <TextField
                                                value={editedPackage.package_name || pkg.package_name}
                                                onChange={(e) => handleChange(pkg.id, 'package_name', e.target.value)}
                                                size="small"
                                            />
                                        ) : pkg.package_name}
                                    </TableCell>
                                    
                                    {['device_group', 'time_sync', 'sensors', 'gps', 'temperature',
                                    'brightness', 'log_access', 'log_download', 'data_user', 'schedule',
                                    'calendar_schedule', 'video', 'emegency', 'email', 'sms'].map((field) => (
                                        <TableCell key={field}>
                                            {editId === pkg.id ? (
                                                <Switch
                                                    checked={editedPackage[field] ?? pkg[field]}
                                                    onChange={(e) => handleChange(pkg.id, field, e.target.checked)}
                                                />
                                            ) : (
                                                <Switch checked={pkg[field]} disabled />
                                            )}
                                        </TableCell>
                                    ))}
                                    
                                    <TableCell>
                                        {editId === pkg.id ? (
                                            <TextField
                                                type="number"
                                                value={editedPackage.media ?? pkg.media}
                                                onChange={(e) => handleChange(pkg.id, 'media', parseInt(e.target.value))}
                                                size="small"
                                            />
                                        ) : pkg.media}
                                    </TableCell>
                                    
                                    <TableCell>
                                        {editId === pkg.id ? (
                                            <>
                                                <Button 
                                                    variant="contained" 
                                                    size="small"
                                                    onClick={() => handleUpdate({ ...pkg, ...editedPackage })}
                                                    disabled={isProcessing}
                                                >
                                                    Update
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    sx={{ ml: 1 }}
                                                    onClick={() => setEditId(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <IconButton onClick={() => {
                                                setEditId(pkg.id);
                                                setEditedPackage({ ...pkg });
                                            }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Fab 
                    color="primary" 
                    aria-label="add"
                    sx={{ 
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        bgcolor: '#1976d2',
                        '&:hover': { bgcolor: '#1565c0' }
                    }}
                    onClick={() => handleCreate()}
                >
                    <Add />
                </Fab>
            </Box>
        </Box>
    );
};

export default AdminPackages;